import { useEffect, useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Building2, Check, ExternalLink, Heart, RotateCcw, Sparkles, Split, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { SearchableSelect } from "@/components/ui/SearchableSelect";
import { useTaskPolling } from "../../hooks";
import { TaskLog } from "../ui";
import {
  createOffice,
  matchOwnerOffices,
  type JstOfficeProposal,
  type JstRegistryMatch,
  type OfficeSourceBase,
  type OwnerOfficeMatchProposal,
  type OwnerOfficeMatchResult,
} from "../../api";
import type {
  WypisExtractionProposal, WypisOrderParcelOwnerBrief, WypisProposalOwner, WypisProposalParcel,
} from "../../types";
import {
  canonicalizeMarital,
  nameKey,
  ownerSlots,
  ownerUnits,
  percentText,
  unitShare,
  unitShareSum,
} from "./maritalUnits";
import { OwnerOfficePickerModal, type PickedOffice } from "./OwnerOfficePickerModal";
import { addrRoundTrips, joinAddress, splitAddress } from "./addressSplit";
import { ExtractionNotes } from "./ExtractionNotes";
import { parsePageSpec } from "./pageSpec";

// addr_split: UI-only — adres edytowany w 4 polach strukturalnych zamiast
// zbiorczego inputa (auto, gdy join+split byłby stratny); wycinany przy zapisie.
type EditableOwner = WypisProposalOwner & { addr_split?: boolean };
type Row = Omit<WypisProposalParcel, "owners"> & { include: boolean; owners: EditableOwner[] };

// Jednostka rejestrowa (G.379 / 12.G) podszyta pod grupę w STARYCH, zapisanych już
// propozycjach — rozdziel przy wczytaniu (nowe ekstrakcje normalizuje backend).
const splitLegacyCode = (value: string): { group: string; unit: string } =>
  /^(g[\s.\-]*\d+|\d+[\s.\-]*g)$/i.test((value || "").trim())
    ? { group: "", unit: value.trim() }
    : { group: value || "", unit: "" };

/** Kolor sumy udziałów: zielony tylko przy pełnych, odczytanych 100%. */
const shareSumTone = (sum: { total: number; unknown: number }): string =>
  sum.unknown > 0 || Math.abs(sum.total - 1) > 0.005
    ? sum.unknown === 0 && sum.total > 1
      ? "text-red-600"
      : "text-amber-600"
    : "text-green-600";

const officeSnapshot = (o: JstRegistryMatch): Partial<WypisProposalOwner> => ({
  kind: "office",
  office_id: o.id,
  full_name: o.name,
  street: o.street ?? "",
  building_no: o.building_no ?? "",
  postal_code: o.postal_code ?? "",
  city: o.city ?? "",
});

// The modal is source-agnostic: a wypis order (order.extraction_proposal + result files)
// and a single manually-added wypis (Działki tab) both feed it through this shape, so the
// review UI — including office-matching — is written once.
export interface ExtractionReviewSource {
  kind: "order" | "wypis";
  id: string;
  proposal: WypisExtractionProposal;
  costUsd: string | null;
  // Options for the "assign a działka" picker on unmatched rows.
  parcelOptions: { value: string; label: string }[];
  // result_file_id → page count, to flag page specs that run past the file.
  pageCounts: Record<string, number>;
  // Which viewset carries the office-matching endpoints for this source.
  officeBase: OfficeSourceBase;
  apply: (rows: WypisProposalParcel[]) => Promise<void>;
  applyPending: boolean;
  // Aktualni właściciele działek (zamówienie): parcel_id → lista. Z nich liczymy
  // podgląd „zostają / nowi / nieaktualni” — ponowny wypis po zmianie właścicieli.
  currentOwners?: Record<string, WypisOrderParcelOwnerBrief[]>;
}

// AI proposes → user adjusts → apply (AronWriteProposal idiom): every cell is
// editable local state layered over the stored proposal; unmatched rows get a
// parcel picker; unchecked rows are skipped on apply.
export function ExtractionReviewModal({ source, onClose }: { source: ExtractionReviewSource; onClose: () => void }) {
  const qc = useQueryClient();
  const proposal = source.proposal;
  const [rows, setRows] = useState<Row[]>(
    () => (proposal?.parcels ?? []).map((p) => {
      const s = splitLegacyCode(p.ownership_code);
      return {
        ...p,
        ownership_code: s.group,
        registry_unit: p.registry_unit || s.unit,
        include: p.matched,
        // Stare propozycje (sprzed sanityzacji w ekstrakcji): własna pozycja małżonka
        // oznaczona małżeństwem → jego udział samodzielny (wpis bez grupy).
        owners: canonicalizeMarital(p.owners.map((o) => ({ ...o, addr_split: !addrRoundTrips(o) }))),
      };
    }),
  );
  const [error, setError] = useState("");
  const [officePicker, setOfficePicker] = useState<{ i: number; j: number } | null>(null);

  // ── zbiorcze dopasowanie urzędów (wszyscy właściciele-instytucje naraz) ──
  const [matchTaskId, setMatchTaskId] = useState<string | null>(null);
  const [matchNote, setMatchNote] = useState("");
  const [officeProposals, setOfficeProposals] = useState<OwnerOfficeMatchProposal[]>([]);
  const matchTask = useTaskPolling(matchTaskId);

  const pendingInstitutional = rows.reduce(
    (n, r) => n + r.owners.filter((o) => o.kind === "office" && !o.office_id && !o.redundant).length,
    0,
  );

  const startMatch = useMutation({
    mutationFn: () =>
      matchOwnerOffices(source.officeBase, source.id, rows.map((r, i) => ({
        row_index: i,
        parcel_id: r.parcel_id,
        teryt: r.teryt,
        ownership_code: r.ownership_code,
        // Pominięci (redundant) nie dostają urzędu; owner_index pozostaje
        // ORYGINALNYM indeksem w r.owners — wynik taska adresuje stan po nim.
        owners: r.owners
          .map((o, j) => ({ o, j }))
          .filter(({ o }) => !o.redundant)
          .map(({ o, j }) => ({
            owner_index: j, kind: o.kind, full_name: o.full_name, office_id: o.office_id ?? null,
          })),
      }))),
    onSuccess: (r) => {
      setMatchNote("");
      setOfficeProposals([]);
      setMatchTaskId(r.task_id);
    },
    onError: () => setMatchNote("Nie udało się uruchomić dopasowania."),
  });

  useEffect(() => {
    if (!matchTaskId || !matchTask.data) return;
    const s = matchTask.data.status;
    if (s === "success") {
      const result = (matchTask.data.result ?? { assigned: [], proposals: [], skipped: [] }) as OwnerOfficeMatchResult;
      for (const a of result.assigned) setOwner(a.row_index, a.owner_index, officeSnapshot(a.office));
      const withProposal = result.proposals.filter((p) => p.proposal);
      setOfficeProposals(withProposal);
      const unresolved = result.skipped.length + (result.proposals.length - withProposal.length);
      setMatchNote(
        `Dopasowano z rejestru: ${result.assigned.length}` +
        (withProposal.length ? ` · propozycje z internetu do zatwierdzenia: ${withProposal.length}` : "") +
        (unresolved ? ` · bez dopasowania (ręcznie): ${unresolved}` : ""),
      );
      setMatchTaskId(null);
    } else if (["failure", "cancelled"].includes(s)) {
      setMatchNote("Dopasowanie nie powiodło się — spróbuj ponownie lub dopasuj pojedynczo.");
      setMatchTaskId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchTask.data?.status]);
  const matching = startMatch.isPending || !!matchTaskId;

  const setProposalField = (key: string, field: keyof JstOfficeProposal, value: string) =>
    setOfficeProposals((s) =>
      s.map((p) => (p.key === key && p.proposal ? { ...p, proposal: { ...p.proposal, [field]: value } } : p)),
    );

  const addProposedOffice = useMutation({
    mutationFn: (p: OwnerOfficeMatchProposal) =>
      createOffice({
        name: p.proposal!.name,
        kind: p.proposal!.kind || p.office_kind,
        department: p.proposal!.department,
        street: p.proposal!.street,
        building_no: p.proposal!.building_no,
        postal_code: p.proposal!.postal_code,
        city: p.proposal!.city,
        phone: p.proposal!.phone,
        email: p.proposal!.email,
        notes: [
          p.proposal!.website ? `WWW: ${p.proposal!.website}` : "",
          p.proposal!.source ? `Dodano z wyszukiwania AI (źródło: ${p.proposal!.source})` : "Dodano z wyszukiwania AI",
        ].filter(Boolean).join("\n"),
      }),
    onSuccess: (office, p) => {
      qc.invalidateQueries({ queryKey: ["pp", "offices"] });
      for (const ref of p.owners) {
        setOwner(ref.row_index, ref.owner_index, officeSnapshot(office as unknown as JstRegistryMatch));
      }
      setOfficeProposals((s) => s.filter((x) => x.key !== p.key));
    },
    onError: () => setMatchNote("Nie udało się dodać urzędu — uzupełnij brakujące pola adresu propozycji."),
  });

  const parcelOptions = source.parcelOptions;
  const fileNames = useMemo(
    () => Object.fromEntries((proposal?.files ?? []).map((f) => [f.result_file_id, f.filename])),
    [proposal],
  );
  const pageCounts = source.pageCounts;

  const setRow = (i: number, patch: Partial<Row>) =>
    setRows((s) => s.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const setOwner = (i: number, j: number, patch: Partial<EditableOwner>) =>
    setRows((s) =>
      s.map((r, idx) =>
        idx === i
          ? { ...r, owners: r.owners.map((o, oj) => (oj === j ? { ...o, ...patch } : o)) }
          : r,
      ),
    );
  // Udział jednostki zgody: u małżonków jedno pole edytuje udział OBOJGA — para ma
  // w wypisie jeden wspólny udział, a rozjazd między wierszami zawyżyłby sumę.
  const setUnitShare = (i: number, unit: number[], share: string) =>
    setRows((s) =>
      s.map((r, idx) =>
        idx === i
          ? { ...r, owners: r.owners.map((o, oj) => (unit.includes(oj) ? { ...o, share } : o)) }
          : r,
      ),
    );

  // ── ręczna poprawka małżeństw (małżeństwo = jeden podpis, jeden wspólny udział) ──
  // Model potrafi zgarnąć do „małżeństwa" osobę z sąsiedniej pozycji Lp albo
  // rozłączyć parę; tu się to odkręca bez ponownej ekstrakcji. Menu per wiersz (⚭).
  const [maritalMenu, setMaritalMenu] = useState<string | null>(null);
  const patchOwners = (i: number, fn: (owners: EditableOwner[]) => EditableOwner[]) =>
    setRows((s) => s.map((r, idx) => (idx === i ? { ...r, owners: fn(r.owners) } : r)));
  const removeOwner = (i: number, j: number) => patchOwners(i, (owners) => owners.filter((_, oj) => oj !== j));
  const nextMaritalGroup = (owners: EditableOwner[]): string => {
    let n = 1;
    while (owners.some((o) => (o.marital_group ?? "") === `man.M${n}`)) n += 1;
    return `man.M${n}`;
  };
  /** Dwie osoby → jedno małżeństwo (wspólna grupa; udział wspólny z pierwszego niepustego). */
  const joinMarital = (i: number, j: number, k: number) =>
    patchOwners(i, (owners) => {
      const group = nextMaritalGroup(owners);
      return owners.map((o, oj) => (oj === j || oj === k ? { ...o, marital_group: group } : o));
    });
  /** Wyłącz wpisy małżeńskie `js` z ich małżeństw (osobny podpis). Osoba mająca obok
   *  wpis samodzielny (techniczny duplikat) traci wpis małżeński — jej własna pozycja
   *  zostaje jedynym wpisem; bez duplikatu wpis po prostu traci grupę. Samotny
   *  współmałżonek wyświetla się dalej jako pojedynczy właściciel. */
  const detachMarital = (i: number, js: number[]) =>
    patchOwners(i, (owners) => {
      const units = ownerUnits(owners);
      const remove = new Set<number>();
      const clear = new Set<number>();
      for (const j of js) {
        const unit = units.find((u) => u.marital.includes(j));
        const hasSolo = !!unit && unit.solo.some((s) => nameKey(owners[s]) === nameKey(owners[j]));
        (hasSolo ? remove : clear).add(j);
      }
      return owners
        .map((o, oj) => (clear.has(oj) ? { ...o, marital_group: "" } : o))
        .filter((_, oj) => !remove.has(oj));
    });
  /** Techniczny duplikat osoby: jej udział samodzielny obok wspólności — nowy wiersz
   *  bez grupy z tym samym adresem, udział do wpisania. Apply składa go w own_share. */
  const addSoloEntry = (i: number, j: number) =>
    patchOwners(i, (owners) => {
      const solo: EditableOwner = {
        ...owners[j], share: "", marital_group: "", office_id: null,
        redundant: false, redundant_with: "", redundant_reason: "", redundant_source: "",
      };
      return [...owners.slice(0, j + 1), solo, ...owners.slice(j + 1)];
    });

  const included = rows.filter((r) => r.include && r.parcel_id);

  const submit = () => {
    setError("");
    source
      .apply(included.map(({ include: _include, ...rest }) => ({
        ...rest,
        // Pominięci (redundant) nie są zapisywani; addr_split to stan czysto UI.
        owners: rest.owners
          .filter((o) => !o.redundant)
          .map(({ addr_split: _addrSplit, ...o }) => o),
      })))
      .then(onClose)
      .catch((e: unknown) => {
        const detail =
          (e as { response?: { data?: { errors?: { detail?: unknown } } } })?.response?.data
            ?.errors?.detail;
        setError(typeof detail === "string" ? detail : "Nie udało się zapisać danych.");
      });
  };

  if (!proposal) return null;

  return (
    <Modal open onClose={onClose} title="Dane z wypisów — propozycja AI do weryfikacji" size="xl">
      <div className="space-y-4">
        <div className="flex items-center justify-between text-xs text-[var(--color-text-secondary)]">
          <span className="flex items-center gap-1">
            <Sparkles size={13} />
            Model: <b>{proposal.model}</b> · plików: {proposal.files.length} · rozpoznanych działek:{" "}
            {rows.length}
          </span>
          <span className="flex items-center gap-3">
            {(pendingInstitutional > 0 || matching) && (
              <Button
                size="sm"
                variant="secondary"
                loading={matching}
                disabled={matching}
                title="Dopasuj urzędy do wszystkich właścicieli-instytucji naraz: rejestr, a gdy brak dopasowania — wyszukiwanie w internecie (AI). Jedna gmina/powiat = jedno wyszukanie."
                onClick={() => startMatch.mutate()}
              >
                {matching ? "Dopasowuję urzędy…" : `Dopasuj urzędy (AI) — ${pendingInstitutional}`}
              </Button>
            )}
            {source.costUsd && (
              <span>Koszt analizy: <b>{Number(source.costUsd).toFixed(4)} USD</b></span>
            )}
          </span>
        </div>
        <ExtractionNotes notes={proposal.notes} />

        {matching && (
          <div className="text-[11px] text-[var(--color-text-muted)] space-y-0.5">
            <div className="flex items-center gap-1">
              <Sparkles size={11} className="animate-pulse" /> Dopasowuję urzędy do właścicieli…
            </div>
            <TaskLog log={matchTask.data?.log ?? ""} />
          </div>
        )}
        {matchNote && !matching && (
          <div className="text-[11px] text-[var(--color-text-secondary)]">{matchNote}</div>
        )}

        {officeProposals.length > 0 && !matching && (
          <div className="space-y-2">
            <span className="section-label flex items-center gap-1">
              <Sparkles size={12} /> Proponowane urzędy z internetu — zweryfikuj i dodaj
            </span>
            {officeProposals.map((p) => (
              <div key={p.key} className="border border-dashed border-[var(--color-border-strong)] p-3 space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span>
                    <b>{p.label}</b> · {p.unit_name} · właścicieli: {p.owners.length}
                  </span>
                  {(p.proposal!.contact_url || p.proposal!.website || p.proposal!.source) && (
                    <a
                      href={p.proposal!.contact_url || p.proposal!.website || p.proposal!.source}
                      target="_blank"
                      rel="noreferrer"
                      title={p.proposal!.contact_url || p.proposal!.website || p.proposal!.source}
                      className="inline-flex items-center gap-1 text-[11px] text-[var(--color-primary)] hover:underline"
                    >
                      <ExternalLink size={11} /> sprawdź adres/kontakt
                    </a>
                  )}
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {([
                    ["name", "Nazwa urzędu *"], ["department", "Wydział / komórka"],
                    ["street", "Ulica *"], ["building_no", "Nr budynku *"],
                    ["postal_code", "Kod pocztowy *"], ["city", "Miejscowość *"],
                    ["phone", "Telefon"], ["email", "E-mail"],
                  ] as [keyof JstOfficeProposal, string][]).map(([field, fieldLabel]) => (
                    <label key={field} className="block">
                      <span className="section-label">{fieldLabel}</span>
                      <input
                        className="input-base mt-0.5 text-xs"
                        value={String(p.proposal![field] ?? "")}
                        onChange={(e) => setProposalField(p.key, field, e.target.value)}
                      />
                    </label>
                  ))}
                </div>
                <div className="flex justify-end gap-2">
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => setOfficeProposals((s) => s.filter((x) => x.key !== p.key))}
                  >
                    Odrzuć
                  </Button>
                  <Button
                    size="sm"
                    loading={addProposedOffice.isPending}
                    disabled={!p.proposal!.name || !p.proposal!.street || !p.proposal!.building_no
                      || !p.proposal!.postal_code || !p.proposal!.city}
                    onClick={() => addProposedOffice.mutate(p)}
                  >
                    Dodaj urząd i przypisz ({p.owners.length})
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3 text-[11px] text-[var(--color-text-secondary)]">
          <span>
            Znalezione działki: <b>{rows.length}</b>, zaznaczone do zapisu:{" "}
            <b>{rows.filter((r) => r.include).length}</b>
          </span>
          <button
            type="button"
            className="text-[var(--color-primary)] hover:underline"
            onClick={() => setRows((s) => s.map((r) => ({ ...r, include: true })))}
          >
            zaznacz wszystkie
          </button>
          <button
            type="button"
            className="text-[var(--color-primary)] hover:underline"
            onClick={() => setRows((s) => s.map((r) => ({ ...r, include: false })))}
          >
            odznacz wszystkie
          </button>
        </div>

        <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
          {rows.map((row, i) => (
            <div
              key={`${row.teryt}-${i}`}
              className={`border p-2 space-y-2 ${
                row.include ? "border-[var(--color-border-strong)]" : "border-[var(--color-border)] opacity-60"
              }`}
            >
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={row.include}
                  onChange={() => setRow(i, { include: !row.include })}
                />
                <span className="font-mono text-xs font-bold">{row.teryt}</span>
                {row.matched ? (
                  <Badge color="green">dopasowana</Badge>
                ) : (
                  <Badge color="red">wymaga przypisania</Badge>
                )}
                {row.pages.map((p, pi) => {
                  const spec = parsePageSpec(p.pages);
                  const max = pageCounts[p.result_file_id] ?? 0;
                  const bad = !spec.valid || (max > 0 && spec.pages.some((n) => n > max));
                  return (
                    <span
                      key={pi}
                      className={`text-[10px] font-mono px-1 border ${bad ? "border-red-400 text-red-600" : "border-[var(--color-border)]"}`}
                      title={fileNames[p.result_file_id] ?? ""}
                    >
                      str. {p.pages}
                    </span>
                  );
                })}
              </div>

              {row.include && row.parcel_id && (source.currentOwners?.[row.parcel_id]?.length ?? 0) > 0 && (
                <OwnerDiff current={source.currentOwners![row.parcel_id]} owners={row.owners} />
              )}

              {!row.matched && row.include !== false && (
                <SearchableSelect
                  options={parcelOptions}
                  value={row.parcel_id ?? ""}
                  placeholder="Przypisz działkę z zamówienia…"
                  onChange={(e) => setRow(i, { parcel_id: e.target.value || null })}
                />
              )}

              <div className="grid grid-cols-4 gap-2">
                <label className="block">
                  <span className="section-label">Nr KW</span>
                  <input
                    className="input-base mt-0.5 font-mono text-xs"
                    value={row.kw_number}
                    onChange={(e) => setRow(i, { kw_number: e.target.value })}
                  />
                </label>
                <label className="block">
                  <span className="section-label">Użytki</span>
                  <input
                    className="input-base mt-0.5 text-xs"
                    value={row.land_use}
                    onChange={(e) => setRow(i, { land_use: e.target.value })}
                  />
                </label>
                <label className="block">
                  <span className="section-label">Grupa rejestrowa</span>
                  <input
                    className="input-base mt-0.5 text-xs"
                    value={row.ownership_code}
                    placeholder="np. 4, 1, 7"
                    onChange={(e) => setRow(i, { ownership_code: e.target.value })}
                  />
                  {row.registry_unit && (
                    <span
                      className="block mt-0.5 text-[10px] text-[var(--color-text-muted)]"
                      title="Jednostka rejestrowa (JRG) z wypisu — to NIE jest grupa rejestrowa"
                    >
                      jednostka rej.: <span className="font-mono">{row.registry_unit}</span>
                    </span>
                  )}
                </label>
                <label className="block">
                  <span className="section-label">Powierzchnia [m²]</span>
                  <input
                    className="input-base mt-0.5 text-xs"
                    type="number"
                    value={row.area_m2 ?? ""}
                    onChange={(e) =>
                      setRow(i, { area_m2: e.target.value === "" ? null : Number(e.target.value) })
                    }
                  />
                </label>
              </div>

              <div className="space-y-1">
                <div className="flex items-center gap-3">
                  <span className="section-label">
                    Właściciele / władający ({row.owners.length}
                    {row.owners.some((o) => o.redundant)
                      ? `, pominiętych: ${row.owners.filter((o) => o.redundant).length}`
                      : ""})
                  </span>
                  {(() => {
                    const sum = unitShareSum(row.owners);
                    if (!sum.units) return null;
                    return (
                      <span
                        className={`text-[11px] font-mono ${shareSumTone(sum)}`}
                        title="Suma udziałów liczona po jednostkach zgody — małżonkowie ze wspólności ustawowej mają JEDEN wspólny udział i liczą się raz (udział samodzielny małżonka dolicza się do ich jednostki). Podpisów = jednostek zgody."
                      >
                        suma udziałów: {percentText(sum.total)}
                        {sum.unknown > 0 && ` (nieczytelnych: ${sum.unknown})`}
                        <span className="text-[var(--color-text-muted)]"> · podpisów: {sum.units}</span>
                      </span>
                    );
                  })()}
                </div>
                {(() => {
                  const units = ownerUnits(row.owners);
                  return ownerSlots(row.owners).map(({ j, owner, unit, first, head, married, solo, duplicate, warning }) => {
                  const menuKey = `${i}:${j}`;
                  const isPerson = owner.kind === "person" && !owner.redundant;
                  const ownUnit = units.find((u) => u.marital.includes(j));
                  const hasSolo = !!ownUnit && ownUnit.solo.some((s) => nameKey(row.owners[s]) === nameKey(owner));
                  // Kandydaci na współmałżonka: inne osoby bez małżeństwa, o innym nazwisku.
                  const spouseOptions = row.owners
                    .map((o, k) => ({ o, k }))
                    .filter(({ o, k }) =>
                      k !== j && o.kind === "person" && !o.redundant && !!o.full_name.trim()
                      && !(o.marital_group ?? "").trim() && nameKey(o) !== nameKey(owner)
                      && !units.some((u) => u.solo.includes(k)));
                  return (
                  <div
                    key={j}
                    className={`space-y-0.5 ${married ? "border-l-2 border-rose-400 pl-1.5" : ""}`}
                  >
                  {married && head && (
                    <span className="block text-[9px] uppercase tracking-wide text-rose-600 dark:text-rose-400">
                      wspólność małżeńska — wspólny udział, jeden podpis
                      {warning && (
                        <span className="ml-2 normal-case tracking-normal text-[11px] text-amber-600">⚠ {warning}</span>
                      )}
                    </span>
                  )}
                  <div
                    className={`grid grid-cols-[90px_1fr_1fr_110px_48px] gap-1 items-center ${
                      owner.redundant ? "opacity-50" : ""
                    }`}
                  >
                    <select
                      className="input-base !py-0.5 text-xs"
                      value={owner.kind}
                      disabled={!!owner.redundant}
                      onChange={(e) => {
                        const kind = e.target.value as "person" | "office";
                        // Powrót na „Osoba" zrywa dopasowanie urzędu — bez tego zapis
                        // i tak utworzyłby właściciela-urząd (stale office_id).
                        setOwner(i, j, kind === "person" ? { kind, office_id: null } : { kind });
                      }}
                    >
                      <option value="person">Osoba</option>
                      <option value="office">Urząd/podmiot</option>
                    </select>
                    <input
                      className={`input-base !py-0.5 text-xs ${owner.redundant ? "line-through" : ""}`}
                      value={owner.full_name}
                      placeholder="Nazwisko i imię / nazwa"
                      disabled={!!owner.redundant}
                      onChange={(e) => setOwner(i, j, { full_name: e.target.value })}
                    />
                    <div className="flex items-center gap-1">
                      <input
                        className="input-base !py-0.5 text-xs flex-1"
                        value={joinAddress(owner)}
                        placeholder="Adres (ulica nr, kod miejscowość)"
                        disabled={!!owner.redundant || !!owner.addr_split}
                        title={owner.addr_split ? "Adres edytowany w polach poniżej" : undefined}
                        onChange={(e) => setOwner(i, j, splitAddress(e.target.value))}
                      />
                      <button
                        type="button"
                        className={`shrink-0 p-0.5 ${
                          owner.addr_split
                            ? "text-[var(--color-primary)]"
                            : "text-[var(--color-text-muted)] hover:text-[var(--color-primary)]"
                        }`}
                        title="Rozbij adres na pola / scal w jedną linię"
                        disabled={!!owner.redundant}
                        onClick={() => setOwner(i, j, { addr_split: !owner.addr_split })}
                      >
                        <Split size={12} />
                      </button>
                    </div>
                    {solo ? (
                      // Udział samodzielny małżonka: własne pole (to NIE jest wspólny udział pary).
                      <input
                        className="input-base !py-0.5 text-xs font-mono italic"
                        value={owner.share}
                        placeholder="Udział samodz."
                        disabled={!!owner.redundant}
                        title="Udział samodzielny tej osoby — obok wspólnego udziału małżeństwa (ten sam podpis)"
                        onChange={(e) => setOwner(i, j, { share: e.target.value })}
                      />
                    ) : married && !first ? (
                      <span
                        className="text-[10px] text-rose-600 dark:text-rose-400 text-center"
                        title="Wspólny udział małżonków — edytuje się go w wierszu powyżej"
                      >
                        wspólny ↑
                      </span>
                    ) : (
                      <input
                        className={`input-base !py-0.5 text-xs font-mono ${
                          married ? "font-semibold text-rose-700 dark:text-rose-400" : ""
                        }`}
                        value={married ? unitShare(row.owners, unit) : owner.share}
                        placeholder="Udział"
                        disabled={!!owner.redundant}
                        title={married ? "Wspólny udział obojga małżonków" : undefined}
                        onChange={(e) => setUnitShare(i, unit, e.target.value)}
                      />
                    )}
                    <div className="flex items-center gap-0.5">
                      {isPerson && (
                        <button
                          type="button"
                          className={`p-0.5 ${
                            married
                              ? "text-rose-600 dark:text-rose-400"
                              : "text-[var(--color-text-muted)] hover:text-rose-600"
                          }`}
                          title={
                            married
                              ? "Małżeństwo: wyłącz z małżeństwa / udział samodzielny / rozłącz"
                              : "Połącz w małżeństwo (wspólny udział, jeden podpis)"
                          }
                          onClick={() => setMaritalMenu((m) => (m === menuKey ? null : menuKey))}
                        >
                          <Heart size={12} />
                        </button>
                      )}
                      <button
                        className="text-[var(--color-text-muted)] hover:text-red-600"
                        title={solo ? "Usuń udział samodzielny (techniczny duplikat osoby)" : "Usuń właściciela"}
                        onClick={() => removeOwner(i, j)}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                  {solo && !owner.redundant && (
                    <div className="pl-[94px] text-[10px] text-rose-600/80 dark:text-rose-400/80">
                      udział samodzielny tej osoby obok wspólności — ta sama osoba, ten sam podpis (techniczny duplikat)
                    </div>
                  )}
                  {duplicate && (
                    <div className="pl-[94px] text-[11px] text-amber-600">
                      Drugi wpis tej samej osoby poza małżeństwem — przy zapisie zostanie pominięty. Jeśli to
                      jej udział samodzielny obok wspólności, połącz pierwszy wpis w małżeństwo (⚭).
                    </div>
                  )}
                  {maritalMenu === menuKey && isPerson && (
                    <div className="flex flex-wrap items-center gap-3 pl-[94px] text-[11px]">
                      {married && !solo ? (
                        <>
                          <button
                            type="button"
                            className="text-[var(--color-primary)] hover:underline"
                            title="Ta osoba nie jest w tym małżeństwie — osobna pozycja, osobny podpis"
                            onClick={() => { detachMarital(i, [j]); setMaritalMenu(null); }}
                          >
                            Wyłącz z małżeństwa
                          </button>
                          {!hasSolo && (
                            <button
                              type="button"
                              className="text-[var(--color-primary)] hover:underline"
                              title="Ta sama osoba ma w wypisie także własną pozycję Lp z udziałem samodzielnym — dodaje jej drugi wiersz (techniczny duplikat)"
                              onClick={() => { addSoloEntry(i, j); setMaritalMenu(null); }}
                            >
                              Dodaj udział samodzielny
                            </button>
                          )}
                          {first && (
                            <button
                              type="button"
                              className="text-[var(--color-primary)] hover:underline"
                              title="Rozwiąż całe małżeństwo — każdy podpisuje osobno"
                              onClick={() => { detachMarital(i, unit); setMaritalMenu(null); }}
                            >
                              Rozłącz małżeństwo
                            </button>
                          )}
                        </>
                      ) : solo ? (
                        <button
                          type="button"
                          className="text-[var(--color-primary)] hover:underline"
                          onClick={() => { removeOwner(i, j); setMaritalMenu(null); }}
                        >
                          Usuń udział samodzielny
                        </button>
                      ) : (
                        <label className="flex items-center gap-1">
                          <span>Wspólność małżeńska z:</span>
                          <select
                            className="input-base !py-0.5 text-xs"
                            value=""
                            onChange={(e) => {
                              if (!e.target.value) return;
                              joinMarital(i, j, Number(e.target.value));
                              setMaritalMenu(null);
                            }}
                          >
                            <option value="">— wybierz współmałżonka —</option>
                            {spouseOptions.map(({ o, k }) => (
                              <option key={k} value={k}>{o.full_name}</option>
                            ))}
                          </select>
                          {!spouseOptions.length && (
                            <span className="text-[var(--color-text-muted)]">brak innych osób bez małżeństwa</span>
                          )}
                        </label>
                      )}
                    </div>
                  )}
                  {owner.redundant && (
                    <div className="flex items-center gap-2 pl-[94px]">
                      <span
                        className="text-[11px] text-amber-600"
                        title={owner.redundant_source === "rule" ? "Reguła systemowa" : "Sugestia AI"}
                      >
                        Pominięty: {owner.redundant_reason ||
                          `podmiot nadrzędny — reprezentuje go ${owner.redundant_with || "inna jednostka"}`}
                      </span>
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-[11px] text-[var(--color-primary)] hover:underline"
                        onClick={() => setOwner(i, j, { redundant: false })}
                      >
                        <RotateCcw size={11} /> Przywróć
                      </button>
                    </div>
                  )}
                  {!owner.redundant && (owner.redundant_reason || owner.redundant_with) && (
                    <div className="flex items-center gap-2 pl-[94px]">
                      <span className="text-[11px] text-[var(--color-text-muted)]">
                        Sugerowano pominięcie: {owner.redundant_reason ||
                          `reprezentuje go ${owner.redundant_with}`}
                      </span>
                      <button
                        type="button"
                        className="text-[11px] text-[var(--color-primary)] hover:underline"
                        onClick={() => setOwner(i, j, { redundant: true })}
                      >
                        Pomiń ponownie
                      </button>
                    </div>
                  )}
                  {!owner.redundant && owner.addr_split && (
                    <div className="grid grid-cols-4 gap-1 pl-[94px]">
                      {([
                        ["street", "Ulica", ""],
                        ["building_no", "Nr budynku / lokalu", ""],
                        ["postal_code", "Kod pocztowy", "00-000"],
                        ["city", "Miejscowość", ""],
                      ] as ["street" | "building_no" | "postal_code" | "city", string, string][]).map(
                        ([field, fieldLabel, ph]) => (
                          <label key={field} className="block">
                            <span className="section-label">{fieldLabel}</span>
                            <input
                              className="input-base mt-0.5 !py-0.5 text-xs"
                              value={owner[field]}
                              placeholder={ph || undefined}
                              onChange={(e) => setOwner(i, j, { [field]: e.target.value })}
                            />
                          </label>
                        ),
                      )}
                    </div>
                  )}
                  {owner.kind === "office" && !owner.redundant && (
                    <div className="flex items-center gap-2 pl-[94px]">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-[11px] text-[var(--color-primary)] hover:underline"
                        onClick={() => setOfficePicker({ i, j })}
                      >
                        <Building2 size={12} /> {owner.office_id ? "Zmień urząd" : "Dopasuj urząd"}
                      </button>
                      {owner.office_id && (
                        <span className="inline-flex items-center gap-1 text-[11px] text-green-600">
                          <Check size={12} /> powiązano z rejestrem
                        </span>
                      )}
                    </div>
                  )}
                  </div>
                  );
                  });
                })()}
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() =>
                    setRows((s) =>
                      s.map((r, idx) =>
                        idx === i
                          ? {
                              ...r,
                              owners: [
                                ...r.owners,
                                { kind: "person", full_name: "", street: "", building_no: "", postal_code: "", city: "", share: "", pesel: "" },
                              ],
                            }
                          : r,
                      ),
                    )
                  }
                >
                  + Dodaj właściciela
                </Button>
              </div>
            </div>
          ))}
        </div>

        {error && <div className="text-xs text-red-600">{error}</div>}

        <div className="flex items-center justify-between">
          <span className="text-xs text-[var(--color-text-secondary)]">
            Do zapisania: <b>{included.length}</b> działek — powstaną właściciele i uzupełnią się
            pola działek
            {source.kind === "order"
              ? ", a PDF-y wypisów trafią do sekcji „Wypisy” projektów."
              : ", a strony wypisu przypną się do działek."}
          </span>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={onClose}>Anuluj</Button>
            <Button onClick={submit} loading={source.applyPending} disabled={included.length === 0}>
              Zastosuj ({included.length})
            </Button>
          </div>
        </div>
      </div>

      {officePicker && (() => {
        const row = rows[officePicker.i];
        const owner = row?.owners[officePicker.j];
        if (!owner) return null;
        return (
          <OwnerOfficePickerModal
            base={source.officeBase}
            sourceId={source.id}
            ownerName={owner.full_name}
            ownershipCode={row.ownership_code}
            teryt={row.teryt}
            parcelId={row.parcel_id}
            onClose={() => setOfficePicker(null)}
            onPick={(o: PickedOffice) => {
              setOwner(officePicker.i, officePicker.j, {
                kind: "office",
                full_name: o.full_name,
                street: o.street,
                building_no: o.building_no,
                postal_code: o.postal_code,
                city: o.city,
                office_id: o.office_id,
              });
              setOfficePicker(null);
            }}
          />
        );
      })()}
    </Modal>
  );
}


// Działka ma już właścicieli: po zapisie zgodny po nazwisku ZOSTAJE (tytuł, WPW, WNU,
// sprawy bez zmian), nowy dostaje rekord, a nieobecny w wypisie — flagę „nieaktualny”
// (nic nie jest kasowane). Liczby w nawiasie mówią, co za nieaktualnym stoi.
const nameKeyOf = (name: string) => name.trim().toLowerCase();

function OwnerDiff({ current, owners }: { current: WypisOrderParcelOwnerBrief[]; owners: EditableOwner[] }) {
  const proposed = owners.filter((o) => !o.redundant && (o.full_name ?? "").trim());
  const proposedKeys = new Set(proposed.map((o) => nameKeyOf(o.full_name)));
  const currentKeys = new Set(current.map((c) => nameKeyOf(c.full_name)));
  const stays = current.filter((c) => proposedKeys.has(nameKeyOf(c.full_name)));
  const outdated = current.filter((c) => !proposedKeys.has(nameKeyOf(c.full_name)));
  const fresh = [...new Map(
    proposed.filter((o) => !currentKeys.has(nameKeyOf(o.full_name))).map((o) => [nameKeyOf(o.full_name), o.full_name.trim()]),
  ).values()];
  const work = (c: WypisOrderParcelOwnerBrief) => {
    const parts = [
      c.titles_secured ? `tytuły: ${c.titles_secured}` : "",
      c.wpw_n ? `WPW: ${c.wpw_n}` : "",
      c.wnu_n ? `WNU: ${c.wnu_n}` : "",
      c.open_cases_n ? `otwarte sprawy: ${c.open_cases_n}` : "",
    ].filter(Boolean);
    return parts.length ? ` (${parts.join(", ")})` : "";
  };
  return (
    <div className="border border-amber-200 bg-amber-50/60 dark:border-amber-800 dark:bg-amber-900/10 p-2 text-[11px] space-y-0.5">
      <div className="section-label">Obecni właściciele działki ({current.length}) — po zapisie</div>
      <div><b>zostają:</b> {stays.map((c) => c.full_name).join(", ") || "—"}</div>
      <div><b>nowi:</b> {fresh.join(", ") || "—"}</div>
      <div>
        <b>nieaktualni:</b> {outdated.map((c) => `${c.full_name}${work(c)}`).join(", ") || "—"}
        {outdated.length > 0 && (
          <span className="text-[var(--color-text-muted)]"> — rekordy zostają z historią, znikną z karty działki i z uzgodnień</span>
        )}
      </div>
    </div>
  );
}
